import type { ReactNode } from "react";
import { Link } from "wouter";
import type { OriginationPayload } from "@shared/dashboardOrigination";
import type { OverviewDeal } from "@/hooks/useDashboardOverview";
import { Sparkline } from "./Sparkline";
import { Meter } from "./Meter";
import { SERIES_BG, SERIES_BG_SOFT } from "./opsColors";

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function Tile({ href, label, value, visual, sub }: { href: string; label: string; value: number; visual?: ReactNode; sub: string }) {
  return (
    <Link href={href} className="min-w-0 bg-card border border-border rounded-lg px-3 py-1 flex flex-col justify-start overflow-hidden hover:border-foreground/25 transition-colors">
      <span className="shrink-0 text-[11px] font-medium text-muted-foreground truncate">{label}</span>
      <span className="shrink-0 flex items-end gap-2 min-w-0">
        <span className="text-xl font-semibold tabular-nums leading-tight">{value.toLocaleString()}</span>
        {visual}
      </span>
      <span className="text-[11px] text-muted-foreground truncate">{sub}</span>
    </Link>
  );
}

/** The eight origination-first numbers across the top of Operations. */
export function KpiTiles({ o, deals, canStar }: { o: OriginationPayload; deals: OverviewDeal[] | undefined; canStar: boolean }) {
  const t = o.tiles;
  const conv = t.inConversation;
  const coi = t.coiTouches30d;
  const touchesPerWeek = o.weeks.map((_, i) => o.touchesWeekly.email[i] + o.touchesWeekly.meeting[i] + o.touchesWeekly.call[i]);
  const starred = canStar ? t.mvaDelivered.starred : undefined;
  return (
    <div className="h-full grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] auto-rows-fr gap-2 overflow-auto">
      <Tile href="/prospects" label="New prospects · 30d" value={t.newProspects30d}
        visual={<Sparkline values={t.newProspectsWeekly} label="New prospects per week" />} sub="per week, last 12 weeks" />
      <Tile href="/prospects" label="In conversation" value={conv.total}
        visual={<div className="flex-1 min-w-0"><Meter title={`${conv.touched30d} touched in 30 days`} segments={[{ value: conv.touched30d, className: SERIES_BG }, { value: conv.total - conv.touched30d, className: SERIES_BG_SOFT }]} /></div>}
        sub={`${conv.touched30d} touched · ${conv.total - conv.touched30d} not in 30d`} />
      <Tile href="/prospects" label="MVAs open" value={t.mvasOpen.working + t.mvasOpen.waiting}
        visual={<div className="flex-1 min-w-0"><Meter title="In progress vs waiting" segments={[{ value: t.mvasOpen.working, className: SERIES_BG }, { value: t.mvasOpen.waiting, className: SERIES_BG_SOFT }]} /></div>}
        sub={`${t.mvasOpen.working} in progress · ${t.mvasOpen.waiting} waiting`} />
      <Tile href="/prospects" label="MVA delivered" value={t.mvaDelivered.total}
        visual={starred !== undefined ? <div className="flex-1 min-w-0"><Meter title={`${starred} starred`} segments={[{ value: starred, className: SERIES_BG }, { value: t.mvaDelivered.total - starred, className: SERIES_BG_SOFT }]} /></div> : undefined}
        sub={starred !== undefined ? `awaiting a proposal · ${starred} starred` : "awaiting a proposal"} />
      <Tile href="/prospects" label="Prospect touches · 30d" value={t.prospectTouches30d}
        visual={<Sparkline values={touchesPerWeek} label="Prospect touches per week" />} sub="emails, calls, meetings" />
      <Tile href="/website" label="Inbound to triage" value={t.inbound.valuations + t.inbound.replies}
        visual={<div className="flex-1 min-w-0"><Meter title="Valuations vs outreach replies" segments={[{ value: t.inbound.valuations, className: SERIES_BG }, { value: t.inbound.replies, className: SERIES_BG_SOFT }]} /></div>}
        sub={`${plural(t.inbound.valuations, "valuation")} · ${plural(t.inbound.replies, "reply", "replies")}`} />
      <Tile href="/coi" label="COI touchpoints · 30d" value={coi.meetings + coi.calls + coi.emails}
        visual={<Sparkline values={t.coiTouchesWeekly} label="COI touchpoints per week" />}
        sub={`${plural(coi.meetings, "meeting")} · ${plural(coi.calls, "call")} · ${plural(coi.emails, "email")}`} />
      <Tile href="/clients" label="Mandates YTD" value={t.mandatesYtd} sub={`${plural(deals?.length ?? 0, "active deal")}`} />
    </div>
  );
}
